import { scoreOf, isAnswered, needsAction } from "@/components/AuditItemRow";
import { ragOf } from "@/lib/rag";

export const residentStatus = (answered, total) => { if (answered === 0) return "not_started"; if (answered === total) return "complete"; return "in_progress"; };
const hasNote = (saved, i) => (saved.actions?.[i] || "").trim();
const emptyResident = { label: "", answers: {} };

export function residentStats(r, resTpl, resItemsTotal) {
  let yes = 0, answered = 0, actions = 0, na = 0;
  resTpl.forEach((d) => {
    const saved = r.answers[d.key] || {};
    d.items.forEach((_, i) => {
      const v = saved.items?.[i];
      if (isAnswered(v)) answered++;
      if (v === "na") na++;
      yes += scoreOf(v);
      if (needsAction(v) && hasNote(saved, i)) actions++;
    });
  });
  const status = residentStatus(answered, resItemsTotal);
  const applicable = resItemsTotal - na;
  const pct = applicable ? Math.round(yes / applicable * 100) : 0;
  return { yes, answered, actions, na, applicable, status, pct, rag: ragOf(pct) };
}

export function residentImprovements(r, resTpl) {
  return resTpl.flatMap((d) => d.items.filter((_, i) => needsAction((r.answers[d.key] || {}).items?.[i])).map((item) => `${d.title}: ${item}`));
}

function tallyDomain(d, saved) {
  let n = d.items.length, y = 0, actions = 0;
  d.items.forEach((_, i) => { const v = saved.items?.[i]; if (v === "na") n--; y += scoreOf(v); if (needsAction(v) && hasNote(saved, i)) actions++; });
  return { n, y, actions };
}

const PRACTICE_PTS = { met: 1, attention: 0.5, not_met: 0 };
const practiceAnswer = (status) => (status === "not_met" ? "no" : "partial");
const PRACTICE_RAG = { met: "green", attention: "amber", not_met: "red" };

export function practiceLive(areas, practice) {
  const domains = areas.map((a) => {
    const saved = practice[a.key] || {};
    const pts = PRACTICE_PTS[saved.status];
    const answered = pts !== undefined;
    const pct = answered ? Math.round(pts * 100) : 0;
    return { key: a.key, title: a.title, pct, rag: PRACTICE_RAG[saved.status] || "red", answered: answered ? 1 : 0, total: 1, hasNo: saved.status === "not_met", practice: true, status: saved.status, action: (saved.action || "").trim() };
  });
  const done = domains.filter((d) => d.answered);
  const pct = done.length ? Math.round(done.reduce((s, d) => s + d.pct, 0) / done.length) : null;
  const findings = done.filter((d) => d.status !== "met").map((d) => ({ key: `area:${d.key}`, scope: "Care home", domain: d.title, item: "Practice confirmation", answer: practiceAnswer(d.status), action: d.action }));
  return { domains, pct, answered: done.length, total: areas.length, findings, actions: findings.filter((f) => f.action).length };
}

export function blendScore(practicePct, residentPct) {
  const parts = [practicePct, residentPct].filter((x) => x !== null && x !== undefined);
  return parts.length ? Math.round(parts.reduce((a, b) => a + b, 0) / parts.length) : 0;
}

export function liveScore({ tpl, isRes, answers = {}, residents, audited }) {
  let total = 0, yes = 0, actions = 0;
  const domains = tpl.map((d) => {
    const sources = isRes(d.key) ? audited.map((k) => (residents[k] || emptyResident).answers[d.key] || {}) : [answers[d.key] || {}];
    let n = 0, y = 0, answered = 0, hasNo = false;
    sources.forEach((saved) => {
      const t = tallyDomain(d, saved); n += t.n; y += t.y; actions += t.actions;
      d.items.forEach((_, i) => { const v = saved.items?.[i]; if (isAnswered(v)) answered++; if (v === "no") hasNo = true; });
    });
    total += n; yes += y;
    const pct = n ? Math.round(y / n * 100) : 0;
    return { key: d.key, pct, rag: ragOf(pct), answered, total: d.items.length * sources.length, hasNo };
  });
  const overall = total ? Math.round(yes / total * 100) : 0;
  return { overall, residentPct: total ? overall : null, domains, actions, points: yes, applicable: total };
}

function domainFindings(d, saved, keyPrefix, scope) {
  const out = [];
  d.items.forEach((item, i) => { const v = saved.items?.[i]; if (needsAction(v)) out.push({ key: `${keyPrefix}:${d.key}:${i}`, scope, domain: d.title, item, answer: v, action: hasNote(saved, i) }); });
  return out;
}

export function allFindings({ tpl, isRes, answers, residents, residentKeys }) {
  return tpl.flatMap((d) => {
    if (!isRes(d.key)) return domainFindings(d, answers[d.key] || {}, "home", "Care home");
    return residentKeys.flatMap((k) => { const r = residents[k] || emptyResident; return domainFindings(d, r.answers[d.key] || {}, `res-${k}`, r.label || `Resident ${k}`); });
  });
}
